import JointRenderSpecs, { ConnectorMeshSpecs, CutoutMeshSpecs, JointBlankMeshSpecs, JointSectionSpecs, MeshSpecs, NodeAnimationSpecs } from './joint-render-specs';
import { Animation, CSG, MeshBuilder, Vector3, Mesh, TransformNode, Tools, StandardMaterial, Color3, Scene, HemisphericLight, Texture } from "@babylonjs/core";
import { AdvancedDynamicTexture, ColorPicker, Control, Ellipse, Line, Rectangle, Slider, StackPanel, TextBlock } from "@babylonjs/gui";
import * as earcut from "earcut";
import ProfilePointsGenerator from './profile-points-generator';
import { ConnectorType, DuctType } from './joint';
import JointSceneSettings from './joint-scene-settings';
import { Vec2 } from '@jscad/modeling/src/maths/vec2';
import JointScene from './joint-scene';

export default class JointRenderer {
    private jointRs: JointRenderSpecs;
    private jointMaterial: StandardMaterial;

    constructor(renderSpecs: JointRenderSpecs) {
        this.jointRs = renderSpecs;

        this.jointMaterial = new StandardMaterial('joint');
        //C:\Program Files (x86)\Common Files\Autodesk Shared\Materials\Textures\2\Mats
        const galvanizedMetalTexture = new Texture('/assets/Metals.Metal Fabrications.Metal Stairs.Galvanized.png');
        this.jointMaterial.diffuseTexture = galvanizedMetalTexture;
        this.jointMaterial.diffuseColor = Color3.Gray();
    }

    // TODO: Observable is rare in React, but maybe useful here?
    private readonly settingsListeners = new Array<(settings: JointSceneSettings) => void>();

    applySceneSettings = (newSettings: JointSceneSettings) => {
        this.settingsListeners.forEach(l => l(newSettings));
    }

    renderToScene = ({ camera, uiTexture, scene }: JointScene, initiallyFolded: boolean) => {
        camera.radius = this.jointRs.defaultCameraDistance;

        if (this.jointRs.ductType === DuctType.Sheets) {
            this.getMeshForJointBlank(this.jointRs.jointBlankMeshSpecs);
            return;
        }

        //This builds up mesh representing the raw shear to shear sheet of metal with any plasma cutouts removed
        let joint = this.jointRs.cutoutMeshSpecs.reduce((j, c) => {
            return this.removeCutout(j, c);
        }, this.getMeshForJointBlank(this.jointRs.jointBlankMeshSpecs));

        //these will show the notches
        // this.getMaleNotches();
        // this.getFemaleNotches();

        //this subtracts the lead and trail notches for male and female ends
        joint = this.getLtNotchesSubtracted(joint);
        joint = this.getVeeNotchesSubtracted(joint);

        /*
            At this point we have a mesh called "joint" which represents a flat, unformed blank as it might come out of the processor. 
            Note that this mesh already has plasma cutouts removed from it which would happen later int he process.
        */

        //Create formed connector meshes or set to undefined
        const totalLegLengths = this.jointRs.totalLegLengths;
        const c1FormedMesh = this.getFormedConnectorFlangeIfRequired(this.jointRs.c1FormedMeshSpecs, totalLegLengths, 'c1-');
        const c2FormedMesh = this.getFormedConnectorFlangeIfRequired(this.jointRs.c2FormedMeshSpecs, totalLegLengths, 'c2-');

        /*
            Now we also have formed meshes representing connectors 1 and 2 (or undefined if no raw connectors selected). 
            There is no concept yet of legs which can be folded but visually. 
            If we stopped here we would be seeing notched connectors in both the formed and unformed states. 
        */

        /*
            Start locating transform nodes with local to parent positioning.
            These will end up replacing a lot of existing code. 

            Think of the joint in a flat view with the male on top and C1 on the left (as the download spec defines).
            We are parenting this from the male down. On any line, the male if formed before legs are bent. 

            Note that the legs might be formed in male to female end order or vise versa.
            We will therefore need to take that into account ultimately when actually defining and bend animations.  

            Now that we have 1-3 meshes representing,
                1) the joint blank with notches and cutouts removed
                2) the formed connectors with notches removed
            we can break up these meshes into what I'm calling segments. 
            
            These segments can be manipulated individually or collectively.

            The male and female ends will be one segment each. We do this because we don't expect formed connectors to extend beyond the legs. 

            Each of the legs will be broken up into 1-5 segments.
                1) flat c1 portion if not raw
                2) formed c1 portion if not raw
                3) flat c2 portion if not raw
                4) formed c2 portion if not raw
                5) portion of leg between c1 and c2 connectors.
                    a) this will always exist.
                    b) the others are may be undefined.
        */
        const { nodeAnimationSpecs } = this.jointRs;
        const bendNodes = nodeAnimationSpecs.map(nas => new TransformNode(nas.nodeName));
        const [leg4SeamNode, maleBendNode, leg1BendNode, leg2BendNode, leg3BendNode] = bendNodes;
        const femaleSectionNode = new TransformNode("female section node");

        /*
            Set parent relationships. 
            
            Note that the seam end of leg 4 is the root (having no parent). 
            We also have femaleSectionNode which may not stay around after further refactoring.
            We currently use it to be the parent of the female section of the joint mesh. 
        */
        maleBendNode.parent = leg1BendNode;
        leg1BendNode.parent = leg2BendNode;
        leg2BendNode.parent = leg3BendNode;
        leg3BendNode.parent = leg4SeamNode;
        femaleSectionNode.parent = leg4SeamNode;

        //These are relative to their parent. Notice we don't need a bunch of math here. 
        maleBendNode.position.z = this.jointRs.lengthOddLegs; //distance from male bend axis to leg 1 bend axis is simply length of leg 1
        leg1BendNode.position.z = this.jointRs.lengthEvenLegs;
        leg2BendNode.position.z = this.jointRs.lengthOddLegs;
        leg3BendNode.position.z = this.jointRs.lengthEvenLegs;
        leg4SeamNode.position.z = this.jointRs.femaleLength;
        femaleSectionNode.position.z = this.jointRs.femaleLength * -1;

        //This is the root, having no parent. This will position them all. 
        leg4SeamNode.position.z -= this.jointRs.stretchOut / 2;

        //Explain the correlation between these nodes and the joint Blank and verify they line up as expected.
        console.log('Joint bounding box minimum world z: ' + JSON.stringify(joint.getBoundingInfo().boundingBox.minimumWorld.z));
        console.log('female section node absolute z: ' + femaleSectionNode.absolutePosition.z);
        if (joint.getBoundingInfo().boundingBox.minimumWorld.z !== femaleSectionNode.absolutePosition.z) {
            throw new Error("These should be strictly equal")
        }

        /*
            We know the nodes align with the joint mesh. 

            The nodes run down the center of the joint along the world z axis. 

            0,0,0 is the center of the joint from shear to shear along z and from coil width to coil width along x
                 with the bottom (inside) of the joint on 0 y and the thickness of the material being positive y
        */

        //Create marker spheres to locate these nodes visually for now
        //this.CreateTransformNodeMarkers(maleBendNode, leg1BendNode, leg2BendNode, leg3BendNode, leg4SeamNode);

        this.createBendLines(maleBendNode, leg1BendNode, leg2BendNode, leg3BendNode, leg4SeamNode);

        //TODO: At this point, we need to know if we created connector meshes. 
        const c1FlatWidth = c1FormedMesh ? this.jointRs.c1Depths.veeDepth : 0;
        const c2FlatWidth = c2FormedMesh ? this.jointRs.c2Depths.veeDepth : 0;
        const sectionSpecs = this.jointRs.getJointSectionSpecs(c1FlatWidth, c2FlatWidth);
        const jointCsg = CSG.FromMesh(joint);

        //Think of these sections as a tall box encompassing all of a given portion of the joint mesh. We then extract the intersecting mesh.
        //this (male) is the notched male portion of the joint with it's parent node being on the bend axis (we'll bend/rotate on x)
        this.extractJointBlankSectionsWithoutConnectors(sectionSpecs, maleBendNode, jointCsg, leg1BendNode, leg2BendNode, leg3BendNode, leg4SeamNode, femaleSectionNode);

        const connectorMeshes = { flats: new Array<Mesh>(), forms: new Array<Mesh>() };

        this.settingsListeners.push(({ formConnectors }) => {
            connectorMeshes.forms.forEach(f => f.setEnabled(formConnectors));
            connectorMeshes.flats.forEach(f => f.setEnabled(!formConnectors));
        });

        this.extractFlatConnectorSectionMeshes(c1FormedMesh, connectorMeshes, sectionSpecs, leg1BendNode, jointCsg, leg3BendNode, leg2BendNode, leg4SeamNode, c2FormedMesh);

        //We're done with this mesh now
        joint.dispose();

        //We can create these two once and reuse them by re-parenting them
        this.createFormedConnectorMeshSections(sectionSpecs, c1FormedMesh, connectorMeshes, leg1BendNode, leg3BendNode, leg2BendNode, leg4SeamNode, c2FormedMesh);

        // GUI
        this.AddJointLabels(uiTexture, leg4SeamNode, leg1BendNode);
        this.addLightAndColorGuiControls(uiTexture, scene);

        //Animation
        const animations = this.setupAnimations(nodeAnimationSpecs, bendNodes, scene);

        if (initiallyFolded) {
            this.presetToFoldedState(nodeAnimationSpecs, bendNodes);
        }

        let jointIsFolded = initiallyFolded;

        this.settingsListeners.push(({ folded }) => {
            if (folded) {
                if (!jointIsFolded) {
                    beginFoldAnimation();
                    jointIsFolded = true;
                }
            } else {
                if (jointIsFolded) {
                    beginUnfoldAnimation();
                    jointIsFolded = false;
                }
            }
        })

        function beginFoldAnimation() {
            scene.beginAnimation(animations[0].node, animations[0].firstFrame, animations[0].lastFrame, false, 1, () => {
                scene.beginAnimation(animations[1].node, animations[1].firstFrame, animations[1].lastFrame, false, 1, () => {
                    scene.beginAnimation(animations[2].node, animations[2].firstFrame, animations[2].lastFrame, false, 1, () => {
                        scene.beginAnimation(animations[3].node, animations[3].firstFrame, animations[3].lastFrame, false, 1, () => {
                            scene.beginAnimation(animations[4].node, animations[4].firstFrame, animations[4].lastFrame, false);
                        })
                    });
                })
            });
        }

        function beginUnfoldAnimation() {
            scene.beginAnimation(animations[4].node, animations[4].lastFrame, animations[4].firstFrame, false, 1, () => {
                scene.beginAnimation(animations[3].node, animations[3].lastFrame, animations[3].firstFrame, false, 1, () => {
                    scene.beginAnimation(animations[2].node, animations[2].lastFrame, animations[2].firstFrame, false, 1, () => {
                        scene.beginAnimation(animations[1].node, animations[1].lastFrame, animations[1].firstFrame, false, 1, () => {
                            scene.beginAnimation(animations[0].node, animations[0].lastFrame, animations[0].firstFrame, false);
                        })
                    });
                })
            });
        }
    }

    private extractJointBlankSectionsWithoutConnectors(sectionSpecs: JointSectionSpecs, maleBendNode: TransformNode, jointCsg: CSG, leg1BendNode: TransformNode, leg2BendNode: TransformNode, leg3BendNode: TransformNode, leg4SeamNode: TransformNode, femaleSectionNode: TransformNode) {
        this.getSectionIntersectionFromSpecs("male", sectionSpecs.maleSection, maleBendNode, jointCsg);
        this.getSectionIntersectionFromSpecs("leg1 Section", sectionSpecs.oddLegSpecsMid, leg1BendNode, jointCsg);
        this.getSectionIntersectionFromSpecs("leg2 Section", sectionSpecs.evenLegSpecsMid, leg2BendNode, jointCsg);
        this.getSectionIntersectionFromSpecs("leg3 Section", sectionSpecs.oddLegSpecsMid, leg3BendNode, jointCsg);
        this.getSectionIntersectionFromSpecs("leg4 Section", sectionSpecs.evenLegSpecsMid, leg4SeamNode, jointCsg);
        this.getSectionIntersectionFromSpecs("female", sectionSpecs.femaleSection, femaleSectionNode, jointCsg);
    }

    private extractFlatConnectorSectionMeshes(c1FormedMesh: Mesh | undefined, connectorMeshes: { flats: Mesh[]; forms: Mesh[]; }, sectionSpecs: JointSectionSpecs, leg1BendNode: TransformNode, jointCsg: CSG, leg3BendNode: TransformNode, leg2BendNode: TransformNode, leg4SeamNode: TransformNode, c2FormedMesh: Mesh | undefined) {
        /*
            For slip and drive connectors, we'll only be pushing odds or evens into the connectorMeshes array. 
            This is because the slip side doesn't form but we didn't take that into account when getting the legMidSection meshes.
        */

        if (c1FormedMesh) {
            //Get joint blank sections for c1
            const leg1FlatC1 = this.getSectionIntersectionFromSpecs("leg1 c1 flat", sectionSpecs.oddLegSpecsC1, leg1BendNode, jointCsg);
            const leg2FlatC1 = this.getSectionIntersectionFromSpecs("leg2 c1 flat", sectionSpecs.evenLegSpecsC1, leg2BendNode, jointCsg);
            const leg3FlatC1 = this.getSectionIntersectionFromSpecs("leg3 c1 flat", sectionSpecs.oddLegSpecsC1, leg3BendNode, jointCsg);
            const leg4FlatC1 = this.getSectionIntersectionFromSpecs("leg4 c1 flat", sectionSpecs.evenLegSpecsC1, leg4SeamNode, jointCsg);

            if (this.jointRs.shouldFormC1Odds()) {
                connectorMeshes.flats.push(leg1FlatC1);
                connectorMeshes.flats.push(leg3FlatC1);
            }

            if (this.jointRs.shouldFormC1Evens()) {
                connectorMeshes.flats.push(leg2FlatC1);
                connectorMeshes.flats.push(leg4FlatC1);
            }
        }

        if (c2FormedMesh) {
            //Get joint blank sections for c2
            const leg1FlatC2 = this.getSectionIntersectionFromSpecs("leg1 c2 flat", sectionSpecs.oddLegSpecsC2, leg1BendNode, jointCsg);
            const leg2FlatC2 = this.getSectionIntersectionFromSpecs("leg2 c2 flat", sectionSpecs.evenLegSpecsC2, leg2BendNode, jointCsg);
            const leg3FlatC2 = this.getSectionIntersectionFromSpecs("leg3 c2 flat", sectionSpecs.oddLegSpecsC2, leg3BendNode, jointCsg);
            const leg4FlatC2 = this.getSectionIntersectionFromSpecs("leg4 c2 flat", sectionSpecs.evenLegSpecsC2, leg4SeamNode, jointCsg);

            if (this.jointRs.shouldFormC2Odds()) {
                connectorMeshes.flats.push(leg1FlatC2);
                connectorMeshes.flats.push(leg3FlatC2);
            }

            if (this.jointRs.shouldFormC2Evens()) {
                connectorMeshes.flats.push(leg2FlatC2);
                connectorMeshes.flats.push(leg4FlatC2);
            }
        }
    }

    private createFormedConnectorMeshSections(sectionSpecs: JointSectionSpecs, c1FormedMesh: Mesh | undefined, connectorMeshes: { flats: Mesh[]; forms: Mesh[]; }, leg1BendNode: TransformNode, leg3BendNode: TransformNode, leg2BendNode: TransformNode, leg4SeamNode: TransformNode, c2FormedMesh: Mesh | undefined) {
        const oddSectionMesh = this.getSectionMesh('odd leg section', sectionSpecs.oddLegSpecsFull);
        const evenSectionMesh = this.getSectionMesh('even leg section', sectionSpecs.evenLegSpecsFull);

        if (c1FormedMesh) {
            //Get the connector sections for c1 (if we modeled them)
            const c1FormedCsg = CSG.FromMesh(c1FormedMesh);

            if (this.jointRs.shouldFormC1Odds()) {
                connectorMeshes.forms.push(this.getSectionIntersection('leg 1 c1', oddSectionMesh, leg1BendNode, c1FormedCsg));
                connectorMeshes.forms.push(this.getSectionIntersection("leg3 c1", oddSectionMesh, leg3BendNode, c1FormedCsg));
            }

            if (this.jointRs.shouldFormC1Evens()) {
                connectorMeshes.forms.push(this.getSectionIntersection("leg2 c1", evenSectionMesh, leg2BendNode, c1FormedCsg));
                connectorMeshes.forms.push(this.getSectionIntersection("leg4 c1", evenSectionMesh, leg4SeamNode, c1FormedCsg));
            }

            c1FormedMesh.dispose();
        }

        if (c2FormedMesh) {
            //Get the connector sections for c2 (if we modeled them)
            const c2FormedCsg = CSG.FromMesh(c2FormedMesh);

            if (this.jointRs.shouldFormC1Odds()) {
                connectorMeshes.forms.push(this.getSectionIntersection("leg1 c2", oddSectionMesh, leg1BendNode, c2FormedCsg));
                connectorMeshes.forms.push(this.getSectionIntersection("leg3 c2", oddSectionMesh, leg3BendNode, c2FormedCsg));
            }

            if (this.jointRs.shouldFormC1Evens()) {
                connectorMeshes.forms.push(this.getSectionIntersection("leg2 c2", evenSectionMesh, leg2BendNode, c2FormedCsg));
                connectorMeshes.forms.push(this.getSectionIntersection("leg4 c2", evenSectionMesh, leg4SeamNode, c2FormedCsg));
            }

            c2FormedMesh.dispose();
        }

        oddSectionMesh.dispose();
        evenSectionMesh.dispose();
    }

    private presetToFoldedState(nodeAnimationSpecs: NodeAnimationSpecs[], bendNodes: TransformNode[]) {
        nodeAnimationSpecs.forEach((nas, i) => {
            const node = bendNodes[i] as any;
            const targetPropertyPath = nas.targetProperty.split('.');
            node[targetPropertyPath[0]][targetPropertyPath[1]] = nas.targetValue;
            console.log(node[targetPropertyPath[0]][targetPropertyPath[1]]);
        });
    }

    private setupAnimations(nodeAnimationSpecs: NodeAnimationSpecs[], bendNodes: TransformNode[], scene: Scene): NodeAnimation[] {
        const frameRate = 10;

        return nodeAnimationSpecs.map(setAnimationOnNode)

        function setAnimationOnNode({ name, targetProperty, targetValue, animationTime }: NodeAnimationSpecs, i: number): NodeAnimation {
            const animation = new Animation(name, targetProperty, frameRate,
                Animation.ANIMATIONTYPE_FLOAT,
                Animation.ANIMATIONLOOPMODE_CYCLE);
            animation.setKeys(getKeyFrames(targetValue, animationTime));

            const node = bendNodes[i];

            node.animations.push(animation);

            return {
                firstFrame: 0,
                lastFrame: frameRate * animationTime,
                node: node,
            }
        }

        function getKeyFrames(rotationAngle: number, rotationTime: number) {
            return [
                {
                    frame: 0,
                    value: 0,
                },
                {
                    frame: frameRate * rotationTime,
                    value: rotationAngle,
                }
            ]
        }
    }

    private addLightAndColorGuiControls(uiTexture: AdvancedDynamicTexture, scene: Scene) {
        var panel = new StackPanel();
        panel.width = "350px";
        panel.isVertical = true;
        panel.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_RIGHT;
        panel.verticalAlignment = Control.VERTICAL_ALIGNMENT_CENTER;
        uiTexture.addControl(panel);

        const light = scene.getLightByName('light1') as HemisphericLight;

        addSlider(panel, 'Light Direction X', 'lightDirectionX', -150, 150, light.direction.x, value => light.direction.x = value);
        addSlider(panel, 'Light Direction Y', 'lightDirectionY', 0, 300, light.direction.y, value => light.direction.y = value);
        addSlider(panel, 'Light Direction Z', 'lightDirectionZ', -150, 150, light.direction.z, value => light.direction.z = value);
        addSlider(panel, 'Light Intensity', 'lightIntensity', 0, 3, light.intensity, value => light.intensity = value);

        addColorPicker(panel, 'Light Diffuse: ', 'lightDiffuseCP', light.diffuse, value => light.diffuse.copyFrom(value));
        addColorPicker(panel, 'Light Specular: ', 'lightSpecularCP', light.specular, value => light.specular.copyFrom(value));
        addColorPicker(panel, 'Light Ground: ', 'lightGroundCP', light.groundColor, value => light.groundColor.copyFrom(value));

        addColorPicker(panel, 'Joint Diffuse: ', 'jointDiffuseCP', this.jointMaterial.diffuseColor, value => this.jointMaterial.diffuseColor.copyFrom(value));
        addColorPicker(panel, 'Joint Specular: ', 'jointSpecularCP', this.jointMaterial.specularColor, value => this.jointMaterial.specularColor.copyFrom(value));

        function addSlider(panel: StackPanel, label: string, name: string, minimum: number, maximum: number, value: number, callback: (value: number) => void) {
            const textBox = getTextBlock(getLabel(value));
            panel.addControl(textBox);

            const slider = new Slider(name);
            slider.height = '20px';
            slider.minimum = minimum;
            slider.maximum = maximum;
            slider.value = value;
            slider.onValueChangedObservable.add(value => {
                callback(value);
                textBox.text = getLabel(value);
            });
            panel.addControl(slider);

            function getLabel(value: number) {
                return label + ': ' + value.toPrecision(4);
            }
        }

        function addColorPicker(panel: StackPanel, label: string, name: string, color: Color3, callback: (eventData: Color3) => void) {
            const textBox = getTextBlock(getLabel(color));
            panel.addControl(textBox);

            var colorPicker = new ColorPicker(name);
            colorPicker.value = color;
            colorPicker.height = "100px";
            colorPicker.width = "100px";
            colorPicker.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
            colorPicker.onValueChangedObservable.add(color => {
                callback(color);
                textBox.text = getLabel(color);
            });
            panel.addControl(colorPicker);

            function getLabel(color: Color3) {
                return label + ': ' + color.toHexString();
            }
        }

        function getTextBlock(name: string) {
            var textBlock = new TextBlock();
            textBlock.text = name;
            textBlock.color = 'White';
            textBlock.height = "30px";
            return textBlock;
        }
    }

    private AddJointLabels(uiTexture: AdvancedDynamicTexture, leg4SeamNode: TransformNode, leg1BendNode: TransformNode) {
        const c1X = this.jointRs.coilWidth / -2 + this.jointRs.c1Depths.ltDepth;
        const c2X = this.jointRs.coilWidth / 2 - this.jointRs.c2Depths.ltDepth;

        buildConnectedLabel("C1: " + this.jointRs.c1TypeLabel,
            leg4SeamNode,
            new Vector3(c1X, 0, this.jointRs.lengthEvenLegs / 2),
            new Vector3(-10 - this.jointRs.c1Depths.ltDepth, 0, 0)
        );

        buildConnectedLabel(
            "C2: " + this.jointRs.c2TypeLabel,
            leg4SeamNode,
            new Vector3(c2X, 0, this.jointRs.lengthEvenLegs / 2),
            new Vector3(10 + this.jointRs.c2Depths.ltDepth, 0, 0)
        );

        buildConnectedLabel("Male End: " + this.jointRs.lockSeamTypeLabel,
            leg1BendNode,
            new Vector3(0, 0, this.jointRs.lengthOddLegs),
            new Vector3(0, 0, 15)
        );

        buildConnectedLabel("Female End: " + this.jointRs.lockSeamTypeLabel,
            leg4SeamNode,
            Vector3.Zero(),
            new Vector3(0, 0, -15)
        );

        function buildConnectedLabel(message: string, parent: TransformNode, targetPosition: Vector3, labelFromTarget: Vector3) {
            const targetNode = new TransformNode(`Label Target Transform Node (${message})`);
            targetNode.position = targetPosition;
            targetNode.parent = parent;

            const labelNode = new TransformNode(`Label Transform Node (${message})`);
            labelNode.position = targetPosition.add(labelFromTarget);
            labelNode.parent = parent;

            const label = getMeshLabel(labelNode, message, uiTexture);

            var line = new Line();
            line.alpha = .5;
            line.lineWidth = 1;
            line.dash = [5, 10];
            uiTexture.addControl(line);
            line.linkWithMesh(targetNode);
            line.connectedControl = label;
            var endRound = new Ellipse();
            endRound.width = "10px";
            endRound.background = "black";
            endRound.height = "10px";
            endRound.color = "white";
            uiTexture.addControl(endRound);
            endRound.linkWithMesh(targetNode);

            return label;

            function getMeshLabel(mesh: Mesh | TransformNode, message: string, advancedTexture: AdvancedDynamicTexture) {
                var label = new Rectangle(message);
                label.background = "black";
                label.height = "30px";
                label.alpha = .5;
                label.width = message.length * 11 + "px";
                label.cornerRadius = 20;
                label.thickness = 1;
                //label.linkOffsetY = 30;
                advancedTexture.addControl(label);
                label.linkWithMesh(mesh);
                var text1 = new TextBlock();
                text1.text = message;
                text1.color = "white";
                label.addControl(text1)

                return label;
            };
        }
    }

    private createBendLines(maleBendTransformNode: TransformNode, leg1BendTransformNode: TransformNode, leg2BendTransformNode: TransformNode, leg3BendTransformNode: TransformNode, femaleBendTransformNode: TransformNode) {
        const bendLineOptions = {
            points: [
                new Vector3(this.jointRs.coilWidth / -2, 0, 0),
                new Vector3(this.jointRs.coilWidth / 2, 0, 0),
            ]
        };

        const maleBendLine = MeshBuilder.CreateDashedLines('male bend line', bendLineOptions);
        maleBendLine.parent = maleBendTransformNode;

        maleBendLine.clone('leg 1 bend line', leg1BendTransformNode);
        maleBendLine.clone('leg 2 bend line', leg2BendTransformNode);
        maleBendLine.clone('leg 3 bend line', leg3BendTransformNode);
        maleBendLine.clone('female bend line', femaleBendTransformNode);
    }

    private CreateTransformNodeMarkers(maleBendTransformNode: TransformNode, leg1BendTransformNode: TransformNode, leg2BendTransformNode: TransformNode, leg3BendTransformNode: TransformNode, femaleBendTransformNode: TransformNode) {
        const markerOptions = { diameter: 0.75 };
        const maleBendMarker = MeshBuilder.CreateSphere("male bend tnode marker", markerOptions);
        const leg1BendMarker = MeshBuilder.CreateSphere("leg 1 bend tnode marker", markerOptions);
        const leg2BendMarker = MeshBuilder.CreateSphere("leg 2 bend tnode marker", markerOptions);
        const leg3BendMarker = MeshBuilder.CreateSphere("leg 3 bend tnode marker", markerOptions);
        const femaleBendMarker = MeshBuilder.CreateSphere("female bend tnode marker", markerOptions);

        const markerMaterial = new StandardMaterial("marker material");
        markerMaterial.diffuseColor = new Color3(0, 1, 0.53);

        maleBendMarker.material = markerMaterial;
        leg1BendMarker.material = markerMaterial;
        leg2BendMarker.material = markerMaterial;
        leg3BendMarker.material = markerMaterial;
        femaleBendMarker.material = markerMaterial;

        maleBendMarker.parent = maleBendTransformNode;
        leg1BendMarker.parent = leg1BendTransformNode;
        leg2BendMarker.parent = leg2BendTransformNode;
        leg3BendMarker.parent = leg3BendTransformNode;
        femaleBendMarker.parent = femaleBendTransformNode;
    }

    getMeshForJointBlank({ name, position, ...options }: JointBlankMeshSpecs): Mesh {
        const blankMesh = MeshBuilder.CreateBox(name, options);
        blankMesh.material = this.jointMaterial;
        blankMesh.position = position;

        return blankMesh;
    }

    removeCutout(blankMesh: Mesh, { type, name, position, ...options }: CutoutMeshSpecs) {
        let cutoutMesh;

        switch (type) {
            case 1:
                cutoutMesh = MeshBuilder.CreateBox(name, options);
                break;
            case 2:
                cutoutMesh = MeshBuilder.CreateCylinder(name, options);
                break;
            default:
                throw new Error('Unexpected cutout type');
        }

        cutoutMesh.position = position;

        return this.getThisMeshSubtractedFromThatMesh(cutoutMesh, blankMesh);
    }

    getThisMeshSubtractedFromThatMesh(thisMesh: Mesh, thatMesh: Mesh) {
        const thatCsg = CSG.FromMesh(thatMesh);
        const thisCsg = CSG.FromMesh(thisMesh);
        const resultingCsg = thatCsg.subtract(thisCsg);

        const resultingMesh = resultingCsg.toMesh(thatMesh.name);

        thatMesh.dispose();
        thisMesh.dispose();

        return resultingMesh;
    }

    getConnectorFlange(name: string, points: Vec2[], length: number) {
        const shape: Vector3[] = [];

        points.forEach(p => shape.push(new Vector3(p[0], 0, p[1])));

        const options = {
            shape,
            depth: length
        }

        const flange = MeshBuilder.ExtrudePolygon(name, options, null, earcut);
        flange.rotation.x = Tools.ToRadians(-90);

        return flange;
    }

    getNotchVee() {
        const options = {
            shape: ProfilePointsGenerator.getVeeNotchPoints(),
            depth: 4
        };

        const extruded = MeshBuilder.ExtrudePolygon("Vee", options, null, earcut);

        extruded.rotation.y = Tools.ToRadians(-90);
        extruded.position.y = 2;

        return extruded;
    }

    getLtNotchesSubtracted(joint: Mesh) {
        const femaleNotches = this.getFemaleNotches();

        let newJoint = this.getThisMeshSubtractedFromThatMesh(femaleNotches[0], joint);

        newJoint = this.getThisMeshSubtractedFromThatMesh(femaleNotches[1], newJoint);

        const maleNotches = this.getMaleNotches();

        newJoint = this.getThisMeshSubtractedFromThatMesh(maleNotches[0], newJoint);

        newJoint = this.getThisMeshSubtractedFromThatMesh(maleNotches[1], newJoint);

        return newJoint;
    }

    getVeeNotchesSubtracted(joint: Mesh) {
        const notches = this.getVeeNotchesForJointBlank();

        notches.forEach(notch => {
            joint = this.getThisMeshSubtractedFromThatMesh(notch, joint);
        })

        return joint;
    }

    getFemaleNotches() {
        const notchLtC2 = this.getNotchLt();

        notchLtC2.scaling.z = this.jointRs.femaleLength;
        notchLtC2.position.z = -this.jointRs.stretchOut / 2 + this.jointRs.femaleLength / 2;
        notchLtC2.position.x = this.jointRs.coilWidth / 2 - this.jointRs.c2Depths.ltDepth;
        notchLtC2.rotation.y = Math.PI;

        const notchLtC1 = this.getNotchLt();

        notchLtC1.scaling.z = notchLtC2.scaling.z;
        notchLtC1.position.z = notchLtC2.position.z;
        notchLtC1.position.x = -this.jointRs.coilWidth / 2 + this.jointRs.c1Depths.ltDepth;

        return [notchLtC1, notchLtC2];
    }

    getMaleNotches() {
        const notchLtC2 = this.getNotchLt();

        notchLtC2.scaling.z = this.jointRs.maleLength;
        notchLtC2.position.z = this.jointRs.stretchOut / 2 - this.jointRs.maleLength / 2;
        notchLtC2.position.x = this.jointRs.coilWidth / 2 - this.jointRs.c2Depths.ltDepth;
        notchLtC2.rotation.y = Math.PI;

        const notchLtC1 = this.getNotchLt();

        notchLtC1.scaling.z = notchLtC2.scaling.z;
        notchLtC1.position.z = notchLtC2.position.z;
        notchLtC1.position.x = -this.jointRs.coilWidth / 2 + this.jointRs.c1Depths.ltDepth;

        return [notchLtC1, notchLtC2];
    }

    getNotchLt() {
        const myPath = [
            new Vector3(0, -2, 0),
            new Vector3(0, 2, 0)
        ]

        const options = {
            shape: ProfilePointsGenerator.getLtNotchPoints(),
            path: myPath,
            closeShape: true,
            cap: Mesh.CAP_ALL,
            sideOrientation: Mesh.DOUBLESIDE
        }

        let extruded = MeshBuilder.ExtrudeShape("L&T", options);

        return extruded;
    }

    getVeeNotchesForJointBlank() {
        const notches: Mesh[] = [];

        let c1;
        let c2;

        this.jointRs.bends.forEach(bend => {
            c2 = this.getNotchVee();
            c2.position.z = bend;
            c2.position.x = this.jointRs.coilWidth / 2 - this.jointRs.c2Depths.veeDepth;
            c2.rotation.y = Math.PI / 2;

            c1 = this.getNotchVee();
            c1.position.z = c2.position.z;
            c1.position.x = -this.jointRs.coilWidth / 2 + this.jointRs.c1Depths.veeDepth;

            notches.push(c1);
            notches.push(c2);
        })

        return notches;
    }

    getBendLine(zPosition: number, coilWidth: number, name: string) {
        const points = [
            new Vector3(coilWidth / -2, 0, zPosition),
            new Vector3(coilWidth / 2, 0, zPosition),
        ]
        const line = MeshBuilder.CreateDashedLines(name, { points: points }) as unknown as Mesh & { points: Vector3[] };
        line.points = points;
        return line;
    }

    getSectionIntersectionFromSpecs(sectionName: string, sectionSpecs: MeshSpecs, parent: TransformNode, wholeCsg: CSG) {
        let sectionMesh = this.getSectionMesh(sectionName, sectionSpecs);
        sectionMesh.parent = parent;

        const sectionCsg = CSG.FromMesh(sectionMesh);
        sectionMesh.dispose();

        return this.getThisCsgIntersectWithThatCsg(sectionName, sectionCsg, wholeCsg, parent);
    }

    getSectionIntersection(sectionName: string, sectionMesh: Mesh, parent: TransformNode, wholeCsg: CSG) {
        sectionMesh.parent = parent;

        const sectionCsg = CSG.FromMesh(sectionMesh);
        return this.getThisCsgIntersectWithThatCsg(sectionName, sectionCsg, wholeCsg, parent);
    }

    getSectionMesh(sectionName: string, { position, ...options }: MeshSpecs): Mesh {
        let sectionMesh = MeshBuilder.CreateBox(sectionName, options);
        sectionMesh.position = position;
        return sectionMesh;
    }

    getThisCsgIntersectWithThatCsg(thisName: string, thisCsg: CSG, thatCsg: CSG, parent: TransformNode): Mesh {
        const intersectionMesh = thisCsg.intersect(thatCsg).toMesh(thisName);
        intersectionMesh.parent = parent;
        intersectionMesh.material = this.jointMaterial;
        return intersectionMesh;
    }

    getMeshSection(sectionName: string, sectionWidth: number, sectionDepth: number, sectionPosition: Vector3, mesh: Mesh) {
        let section = MeshBuilder.CreateBox(sectionName, {
            height: 5,
            width: sectionWidth,
            depth: sectionDepth
        });

        section.position.x = sectionPosition.x;
        section.position.y = sectionPosition.y;
        section.position.z = sectionPosition.z;

        return this.getThisMeshIntersectWithThatMesh(section, mesh);
    }

    getThisMeshIntersectWithThatMesh(thisMesh: Mesh, thatMesh: Mesh) {
        const thatCsg = CSG.FromMesh(thatMesh);
        const thisCsg = CSG.FromMesh(thisMesh);
        const resultingCsg = thatCsg.intersect(thisCsg);

        const resultingMesh = resultingCsg.toMesh(thisMesh.name);

        //thatMesh.dispose();
        thisMesh.dispose();

        return resultingMesh;
    }

    getFormedConnectorFlangeIfRequired(connectorMeshSpecs: ConnectorMeshSpecs, totalLegLengths: number, namePrefix: string): Mesh | undefined {
        let mesh: Mesh | undefined;
        let flangeSdMesh: Mesh;
        let flangeTdcMesh: Mesh;

        const getVeeNotchesSubtractedFromFlange = (flange: Mesh, specs: ConnectorMeshSpecs) => {
            const notches = getVeeNotchesForJointConnectors();
            orientConnectorMesh(flange, specs);

            //TODO: This looks like a reduce?
            notches.forEach(notch => {
                flange = this.getThisMeshSubtractedFromThatMesh(notch, flange);
            })

            return flange;

            function orientConnectorMesh(connectorMesh: Mesh, specs: ConnectorMeshSpecs) {
                connectorMesh.rotation.y = specs.rotationY;
                connectorMesh.position = specs.position;
            }
        }

        const getVeeNotchesForJointConnectors = () => {
            const notches: Mesh[] = [];

            const veeNotch = this.getNotchVee();

            this.jointRs.veeNotchJointConnectorMeshSpecs.forEach(bend => {
                let c1 = veeNotch.clone();
                c1.position = bend.c1Position;
                c1.rotation.x = bend.rotationX;
                notches.push(c1);

                let c2 = veeNotch.clone();
                c2.position = bend.c2Position;
                c2.rotation.x = bend.rotationX;
                c2.rotation.y = bend.rotationX * -1;
                notches.push(c2);
            });

            veeNotch.dispose();

            return notches;
        }

        const getSlipAndDriveFlange = () => {
            const name = namePrefix + 'flange-sd';
            if (flangeSdMesh) {
                return flangeSdMesh.clone(name);
            }
            const points = ProfilePointsGenerator.getSlipAndDriveConnectorPoints(this.jointRs.coilThickness);
            return flangeSdMesh = this.getConnectorFlange(name, points, totalLegLengths);
        }

        const getTdcFlange = () => {
            const name = namePrefix + 'flange-tdc';
            if (flangeTdcMesh) {
                return flangeTdcMesh.clone(name);
            }
            const points = ProfilePointsGenerator.getTdcConnectorPoints(this.jointRs.coilThickness);
            return flangeTdcMesh = this.getConnectorFlange(name, points, totalLegLengths);
        }

        switch (connectorMeshSpecs.connectorType) {
            case ConnectorType.SandD: // Slip and Drive
                mesh = getSlipAndDriveFlange();
                break;
            case ConnectorType.TD: // Transverse Duct Flange (assuming TDC)
                mesh = getTdcFlange();
                break;
        }
        /*
            If we created a connector mesh, we need to orient it and remove vee/corner notches from it.
        */
        if (mesh) {
            mesh = getVeeNotchesSubtractedFromFlange(mesh, connectorMeshSpecs);
        }

        return mesh;
    }
}

type NodeAnimation = {
    firstFrame: number,
    lastFrame: number,
    node: TransformNode,
}